/* « Mon Assistant » — Centre quotidien des actions.
 *
 * Réponse à la question « Que dois-je faire aujourd'hui ? »
 * Complémentaire du Poste de commandement (« Comment se porte mon patrimoine ? »).
 *
 * 6 blocs :
 *   1. Greeting IA (résumé du jour)
 *   2. Mes priorités (actions critiques + importantes)
 *   3. Santé du patrimoine (scores par immeuble)
 *   4. Actions rapides (< 5 min)
 *   5. Changements récents (7 derniers jours)
 *   6. Suggestions IA + Objectifs
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sparkles, AlertTriangle, CheckCircle2, Clock, TrendingUp,
  ArrowRight, Zap, Target, Lightbulb, Activity,
} from "lucide-react";

export default function MyAssistantPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/assistant/today").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="text-muted-foreground text-sm">Analyse en cours…</div>;
  const g = data.greeting;

  return (
    <div className="animate-fade-up space-y-8 max-w-6xl" data-testid="my-assistant-page">
      {/* Bloc 1 — Greeting IA */}
      <section
        className="rounded-3xl p-6 lg:p-8 relative overflow-hidden text-white"
        style={{ background: "hsl(158 47% 12%)" }}
        data-testid="assistant-greeting"
      >
        <div className="relative z-10 max-w-3xl">
          <div className="flex items-center gap-2 text-accent text-[10px] uppercase tracking-widest mb-2">
            <Sparkles className="h-3 w-3" /> Mon Assistant
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl">
            Bonjour {g.first_name} 👋
          </h1>
          <p className="mt-3 text-white/85 text-lg">{g.message}</p>
          <p className="mt-2 text-white/70 text-sm">Score global : <strong className="text-white">{g.score}/100</strong>.</p>
          <p className="mt-4 text-white/85 text-sm">Aujourd&apos;hui j&apos;ai identifié :</p>
          <ul className="mt-2 space-y-1 text-white/90 text-sm">
            <li>• <strong data-testid="assistant-criticals">{g.criticals}</strong> action{g.criticals > 1 ? "s" : ""} critique{g.criticals > 1 ? "s" : ""}</li>
            <li>• <strong data-testid="assistant-importants">{g.importants}</strong> action{g.importants > 1 ? "s" : ""} importante{g.importants > 1 ? "s" : ""}</li>
            <li>• <strong data-testid="assistant-optimizations">{g.optimizations}</strong> optimisation{g.optimizations > 1 ? "s" : ""} possible{g.optimizations > 1 ? "s" : ""}</li>
          </ul>
          {g.estimated_minutes > 0 && (
            <p className="mt-4 text-white/70 text-xs flex items-center gap-1.5">
              <Clock className="h-3 w-3" /> Temps estimé : environ {g.estimated_minutes} minutes
            </p>
          )}
        </div>
        <div className="absolute inset-0 opacity-30"
             style={{ background: "radial-gradient(circle at 90% 20%, hsl(36 43% 50% / .4), transparent 55%)" }} />
      </section>

      {/* Bloc 2 — Priorités */}
      <section data-testid="assistant-priorities">
        <SectionTitle icon={AlertTriangle} title="Mes priorités" subtitle="Actions les plus importantes à traiter." />
        {data.priorities.length === 0 ? (
          <EmptyState message="Aucune action prioritaire. Beau travail !" />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {data.priorities.map((p) => <PriorityCard key={`${p.code}-${p.building}-${p.lot}`} p={p} />)}
          </div>
        )}
      </section>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Bloc 3 — Santé */}
        <section data-testid="assistant-health">
          <SectionTitle icon={Activity} title="Santé du patrimoine" />
          {data.health.length === 0 ? (
            <EmptyState message="Aucun immeuble suivi." />
          ) : (
            <ul className="space-y-2">
              {data.health.map((h) => (
                <li key={h.name}>
                  <Link to={h.href} className="flex items-center gap-3 rounded-xl border border-border p-3 hover:border-accent transition-colors">
                    <ScorePill score={h.score} tone={h.tone} />
                    <div className="flex-1 font-medium">{h.name}</div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Bloc 4 — Actions rapides */}
        <section data-testid="assistant-quick-actions">
          <SectionTitle icon={Zap} title="Actions rapides" subtitle="Moins de 5 minutes." />
          {data.quick_actions.length === 0 ? (
            <EmptyState message="Rien à traiter rapidement." />
          ) : (
            <div className="space-y-2">
              {data.quick_actions.map((q) => (
                <Link key={q.code} to={q.href}
                      className="flex items-center gap-3 rounded-xl border border-accent/30 bg-accent/5 hover:bg-accent/10 transition-colors p-3">
                  <div className="rounded-full bg-accent/20 h-8 w-8 flex items-center justify-center font-mono font-bold text-sm">
                    {q.count}
                  </div>
                  <div className="flex-1 font-medium text-sm">{q.label}</div>
                  <ArrowRight className="h-4 w-4 text-accent" />
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Bloc 5 — Changements */}
        <section data-testid="assistant-changes">
          <SectionTitle icon={TrendingUp} title="Ce qui a changé cette semaine" />
          <div className="grid grid-cols-2 gap-3">
            <ChangeCard value={data.changes.rents_collected} label="loyers encaissés" positive />
            <ChangeCard value={data.changes.ocr_new} label="documents OCR" positive />
            <ChangeCard value={data.changes.leases_new} label="nouveaux baux" positive />
            <ChangeCard value={data.changes.alerts_resolved} label="alertes résolues" positive />
          </div>
        </section>

        {/* Bloc 6 — Objectifs */}
        <section data-testid="assistant-objectives">
          <SectionTitle icon={Target} title="Mes objectifs" />
          <div className="space-y-3">
            {data.objectives.map((o) => (
              <div key={o.code} className="rounded-xl border border-border p-4">
                <div className="flex justify-between items-baseline mb-2">
                  <div className="text-sm font-medium">{o.label}</div>
                  <div className="text-xs text-muted-foreground">
                    <strong className="text-foreground text-base">{o.current}{o.unit}</strong> / {o.target}{o.unit}
                  </div>
                </div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-accent transition-all"
                       style={{ width: `${o.progress_pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Bloc 7 — Suggestions IA */}
      {data.suggestions.length > 0 && (
        <section data-testid="assistant-suggestions">
          <SectionTitle icon={Lightbulb} title="Suggestions IA" subtitle="Optimisations que je vous propose." />
          <div className="grid gap-3 md:grid-cols-2">
            {data.suggestions.map((s) => (
              <div key={s.code} className="rounded-xl border border-accent/30 bg-accent/5 p-4">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{s.title}</div>
                    <p className="mt-1 text-xs text-muted-foreground">{s.detail}</p>
                    {s.action_href && (
                      <Button asChild variant="link" size="sm" className="mt-1 p-0 h-auto text-xs">
                        <Link to={s.action_href}>{s.action_label} <ArrowRight className="ml-1 h-3 w-3" /></Link>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-accent" />
        <h2 className="font-heading text-lg">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
}

function PriorityCard({ p }) {
  const dot = p.severity === "critical" ? "🔴"
            : p.severity === "warning" ? "🟠" : "🟡";
  return (
    <div className="rounded-xl border border-border p-4 hover:shadow-sm transition-shadow"
         data-testid={`assistant-priority-${p.code}`}>
      <div className="flex items-start gap-2 mb-2">
        <span className="text-sm">{dot}</span>
        <div className="font-medium text-sm flex-1">{p.title}</div>
      </div>
      {(p.building || p.lot) && (
        <div className="text-xs text-muted-foreground mb-2">
          {p.building}{p.building && p.lot && " · "}{p.lot}
        </div>
      )}
      {p.reason && <p className="text-xs mb-3">{p.reason}</p>}
      <Button asChild size="sm" variant="outline">
        <Link to={p.action_href}>{p.action_label} <ArrowRight className="ml-1 h-3 w-3" /></Link>
      </Button>
    </div>
  );
}

function ScorePill({ score, tone }) {
  const cls = tone === "success" ? "bg-emerald-100 text-emerald-800"
            : tone === "warning" ? "bg-amber-100 text-amber-800"
            : "bg-red-100 text-red-800";
  const emoji = tone === "success" ? "🟢" : tone === "warning" ? "🟠" : "🔴";
  return (
    <div className={`${cls} font-mono font-bold rounded-full text-xs px-3 py-1.5 flex items-center gap-1 flex-shrink-0`}>
      {emoji} {score}/100
    </div>
  );
}

function ChangeCard({ value, label, positive }) {
  return (
    <div className="rounded-xl bg-secondary/30 p-3 text-center">
      <div className={`font-serif text-2xl ${positive ? "text-emerald-700" : "text-foreground"}`}>
        {positive && value > 0 ? "+" : ""}{value}
      </div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
      <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {message}
    </div>
  );
}
